import { Accordion } from 'cd-eft/components/common/Accordion';
import { RadioGroup } from 'cd-eft/components/common/RadioGroup';
import { observer } from 'mobx-react';
import React from 'react';
import { FlowTypeIntegers, VelocityMethodVM } from './VelocityMethodVM';
import { Table } from 'reactstrap';
import { SelectOption } from 'cd-eft/components/common/SelectOption';
import { Input } from 'cd-eft/components/common/Input';
import { ButtonBlock } from 'cd-eft/components/Design/common/ButtonBlock/ButtonBlock';
import Icon from '@mdi/react';
import { mdiClose, mdiLockOpenOutline, mdiLock } from '@mdi/js';
import './velocityMethod.scss';

const outlineButtonClass = ' usa-button--outline';
interface IVelocityInputProps {
  vm: VelocityMethodVM;
}

export const MethodTypeSelection = observer((props: IVelocityInputProps) => {
  if (!props.vm) {
    return null;
  }
  return (
    <div className="row control-radio-group channel-control-group">
      <div className="col sub-column">
        <RadioGroup
          labelId="method-selection"
          label="Method Type"
          items={[
            {
              label: 'Lag',
              id: 'method-lag',
              name: 'method-lag',
              className: 'control-radio-button usa-radio__input--tile',
              onChange: (e) => props.vm.selectMethod(e),
              checked: props.vm.lagSelected,
            },
            {
              label: 'Velocity',
              id: 'method-velocity',
              name: 'method-velocity',
              className: 'control-radio-button usa-radio__input--tile',
              onChange: (e) => props.vm.selectMethod(e),
              checked: props.vm.velocitySelected,
            },
          ]}
        />
      </div>
    </div>
  );
});

export const VelocityAccordion = observer((props: IVelocityInputProps) => {
  return (
    <Accordion headers={['Velocity Method']} className="velocity-accordion" light={true}>
      <div className="col">
        <div className="row switch-row">
          <label htmlFor="draw-flow-segment">Draw Flow Segment</label>
          <ul className="usa-button-group usa-button-group--segmented segment-type-group">
            <li className="usa-button-group__item m-0">
              <button
                disabled={false}
                data-testid="sheet-group-button"
                className={`usa-button${props.vm.activeSegmentButton !== FlowTypeIntegers.SHEET ? outlineButtonClass : ''
                  }`}
                name="segment-group-sheet"
                onClick={props.vm.selectSegmentButton}
                value={FlowTypeIntegers.SHEET}
              >
                Sheet
              </button>
            </li>
            <li className="usa-button-group__item m-0">
              <button
                disabled={false}
                data-testid="shallow-group-button"
                className={`usa-button${props.vm.activeSegmentButton !== FlowTypeIntegers.SHALLOW ? outlineButtonClass : ''
                  }`}
                name="segment-group-shallow"
                onClick={props.vm.selectSegmentButton}
                value={FlowTypeIntegers.SHALLOW}
              >
                Shallow Concentrated
              </button>
            </li>
            <li className="usa-button-group__item m-0">
              <button
                disabled={false}
                data-testid="channel-group-button"
                className={`usa-button${props.vm.activeSegmentButton !== FlowTypeIntegers.CHANNEL ? outlineButtonClass : ''
                  }`}
                name="segment-group-channel"
                onClick={props.vm.selectSegmentButton}
                value={FlowTypeIntegers.CHANNEL}
              >
                Channel
              </button>
            </li>
          </ul>
          <div className="flow-segment-toggle">
            <button
              className={`usa-button usa-button--outline edit-mode-toggle-button ${props.vm.drawFlowSegmentToggle ? 'on' : 'off'
                }`}
              onClick={props.vm.flipFlowSegmentToggle}
            >
              <Icon className="icon" path={props.vm.drawFlowSegmentToggle ? mdiLockOpenOutline : mdiLock} size={1} />
              Edit Mode: {props.vm.drawFlowSegmentToggle ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>
        <div className="row">
          <button
            className="usa-button"
            onClick={props.vm.splitSegmentCallback}
            id="split-segments-btn"
            name="split-segments-btn"
          >
            Split Segments
          </button>
        </div>
        <div className="row">
          <VelocityTable vm={props.vm} />
        </div>
      </div>
    </Accordion>
  );
});

// Currently excluded for pending UI/UX changes
// exported for unit tests. Export can be removed once table is implemented after UI
// changes have been finalized.
export const VelocityTable = observer((props: IVelocityInputProps) => {
  return (
    <div className="row set-table-container velocity-table-container">
      <Table className="set-table velocity-table">
        <thead>
          <tr>
            <th className="vt-inputs-section-header" colSpan={6}>
              Inputs{' '}
              <span className="header-subtext">
                {`(2-Year Rainfall in Inches: ${props.vm.inputHeaderRainfall})`}
              </span>
            </th>
            <th className="results-background vt-results-section-header result-left-edge" colSpan={3}>
              Results
            </th>
          </tr>
          <tr className="velocity-column-headers">
            <th id="flow-type-header">Flow Type</th>
            <th id="length-header">Length (ft)</th>
            <th id="slope-header">Slope (ft/ft)</th>
            <th id="surface-mannings-header">Surface Manning's n</th>
            <th id="n-header">n</th>
            <th id="actions-header">Actions</th>
            {/* Manual and Automated in this format for now. */}
            <th id="velocity-header" className="results-background result-left-edge">
              Velocity (f/s)
            </th>
            <th id="time-header" className="results-background">
              Time (hr)
            </th>
            <th id="totals-header" className="results-background totals-column">
              Totals
            </th>
          </tr>
        </thead>
        <tbody>
          {props.vm.tableData.map((rowData, index) => (
            <tr
              key={index}
              className={props.vm.highlightedRowIndex === index ? 'highlighted' : ''}
              onMouseEnter={() => props.vm.highlightSeparatorAtIndex(index)}
              onMouseLeave={() => props.vm.clearHighlight()}
            >
              <td>
                <SelectOption
                  options={props.vm.tableInputOptions}
                  idAndName={`flow-type-dd-${index}`}
                  value={rowData.flowType}
                  onChange={(e) => props.vm.handleSelectChange(e, index)}
                  className={`velocity-input-wrapper ${props.vm.getErrorClass(
                    props.vm.tableValidationData[index].flowType
                  )}`}
                  onBlur={(e) => props.vm.handleSelectBlur(e, index)}
                  valid={!props.vm.tableValidationData[index].flowType}
                />
              </td>
              <td>
                <Input
                  wrapperClassName="velocity-input-wrapper"
                  className={props.vm.getErrorClass(props.vm.tableValidationData[index].length)}
                  idAndName={`length-${index}`}
                  value={rowData.length}
                  valid={!props.vm.tableValidationData[index].length}
                  onChange={(e) => props.vm.handleInputChange(e, index)}
                  onBlur={(e) => props.vm.handleInputBlur(e, index)}
                  onFocus={(e) => props.vm.handleInputFocus(e, index)}
                  type="number"
                  step={0.1}
                  externalLabel="length-header"
                // placeholder={props.vm.tableData.length - 1 === index ? props.vm.suggestedLength : undefined}// likely a future enhancement
                />
              </td>
              <td>
                <Input
                  wrapperClassName="velocity-input-wrapper"
                  className={props.vm.getErrorClass(props.vm.tableValidationData[index].slope)}
                  idAndName={`slope-${index}`}
                  value={rowData.slope}
                  valid={!props.vm.tableValidationData[index].slope}
                  onChange={(e) => props.vm.handleInputChange(e, index)}
                  onBlur={(e) => props.vm.handleInputBlur(e, index)}
                  type="number"
                  step={0.01}
                />
              </td>
              <td>
                {rowData.flowType === FlowTypeIntegers.SHEET || rowData.flowType === FlowTypeIntegers.SHALLOW ? (
                  <SelectOption
                    options={
                      rowData.flowType === FlowTypeIntegers.SHALLOW ? props.vm.shallowOptions : props.vm.sheetOptions
                    }
                    idAndName={`surface-mannings-${index}`}
                    value={rowData.surfaceMannings}
                    onChange={(e) => props.vm.handleSelectChange(e, index)}
                    className={`velocity-input-wrapper ${props.vm.getErrorClass(
                      props.vm.tableValidationData[index].surfaceMannings
                    )}`}
                    onBlur={(e) => props.vm.handleSelectBlur(e, index)}
                    valid={!props.vm.tableValidationData[index].surfaceMannings}
                  />
                ) : null}
              </td>
              <td>
                {rowData.flowType === FlowTypeIntegers.CHANNEL ? (
                  <Input
                    wrapperClassName="velocity-input-wrapper"
                    className={props.vm.getErrorClass(props.vm.tableValidationData[index].n)}
                    idAndName={`n-${index}`}
                    value={rowData.n}
                    valid={!props.vm.tableValidationData[index].n}
                    onChange={(e) => props.vm.handleInputChange(e, index)}
                    onBlur={(e) => props.vm.handleInputBlur(e, index)}
                    type="number"
                    step={0.0001}
                  />
                ) : null}
              </td>
              <td>
                {rowData.flowType === FlowTypeIntegers.CHANNEL ? (
                  <button
                    className="usa-button usa-button--unstyled"
                    type="button"
                    onClick={() => props.vm.openDetailsContainer(index)}
                  >
                    View Parameters
                  </button>
                ) : null}
              </td>
              <td className="results-background  result-left-edge">
                <Input
                  wrapperClassName="velocity-input-wrapper shorter-inputs"
                  className={props.vm.getErrorClass(props.vm.tableValidationData[index].velocity)}
                  idAndName={`velocity-${index}`}
                  value={rowData.velocity}
                  valid={!props.vm.tableValidationData[index].velocity}
                  onChange={(e) => props.vm.handleInputChange(e, index)}
                  onBlur={(e) => props.vm.handleInputBlur(e, index)}
                  type="number"
                  step={0.01}
                  disabled={rowData.flowType === FlowTypeIntegers.SHEET || rowData.flowType === FlowTypeIntegers.SHALLOW}
                />
              </td>
              <td className="results-background">
                <Input
                  wrapperClassName="velocity-input-wrapper shorter-inputs"
                  className={props.vm.getErrorClass(props.vm.tableValidationData[index].time)}
                  idAndName={`time-${index}`}
                  value={rowData.time}
                  valid={!props.vm.tableValidationData[index].time}
                  onChange={(e) => props.vm.handleInputChange(e, index)}
                  onBlur={(e) => props.vm.handleInputBlur(e, index)}
                  type="number"
                  step={0.01}
                />
              </td>

              {/* ------------------------- TOTALS - stem from top row ------------------------------------- */}
              {props.vm.tableData.length > 0 && index === 0 ? (
                <td className="results-background totals-column" rowSpan={props.vm.tableData.length}>
                  <span className="totals-results-span">
                    <Input
                      label="Length (ft)"
                      wrapperClassName="totals-results"
                      idAndName="totals-length"
                      value={props.vm.totalLengthResult}
                      readOnly={true}
                      valid={true}
                      type="text"
                      onChange={() => { }}
                      step={0.01}
                    />
                  </span>
                  <span className="totals-results-span">
                    <Input
                      label="Velocity (f/s)"
                      wrapperClassName="totals-results"
                      idAndName="totals-velocity"
                      value={props.vm.averageVelocity}
                      readOnly={true}
                      valid={true}
                      type="text"
                      onChange={() => { }}
                    />
                  </span>
                  <span className="totals-results-span">
                    <Input
                      label="Time (hr)"
                      wrapperClassName="totals-results"
                      idAndName="totals-time"
                      value={props.vm.totalTime}
                      valid={!props.vm.miscValidations.totalTime}
                      className={props.vm.getErrorClass(props.vm.miscValidations.totalTime)}
                      type="text"
                      onChange={props.vm.handleMiscInputChange}
                      onBlur={props.vm.handleMiscBlur}
                    />
                  </span>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="vt-totals-row">
            <td className="bordered bordered-left">Total Length</td>
            <td colSpan={1} className="p-0 bordered bordered-right">
              <Input
                wrapperClassName="velocity-total-length-wrapper"
                idAndName="vt-total-length"
                value={props.vm.getFlowPath}
                readOnly={true}
                valid={true}
                type="text"
                onChange={() => { }}
              />
            </td>
            <td colSpan={7} className="empty-cells"></td>
          </tr>
        </tfoot>
      </Table>
      <ChannelDetails {...props} />
    </div>
  );
});

const ChannelDetails = observer((props: IVelocityInputProps) => {
  return (
    <div className="usa-card usa-card__container channel-details-container" hidden={!props.vm.showDetailsContainer}>
      <div className="usa-card__header details-header-row">
        <h4 className="">Enter Channel Details</h4>
        <button
          className="usa-button usa-button--unstyled modal-button"
          title="Close"
          onClick={props.vm.closeDetailsContainer}
        >
          <Icon path={mdiClose} color="black" size={1} />
        </button>
      </div>

      <div className=" usa-card__body channel-details-inputs">
        <Input
          elementLabel={
            <span>
              Cross-Section Area (ft<sup>2</sup>)
            </span>
          }
          idAndName={`area-details-input`}
          value={props.vm.temporaryChannelDetails.area}
          valid={!props.vm.miscValidations.channelArea}
          className={props.vm.getErrorClass(props.vm.miscValidations.channelArea)}
          type="number"
          onChange={(e) => props.vm.channelDetailsChange(e)}
          onBlur={(e) => props.vm.handleMiscBlur(e)}
        />
        <Input
          label={`Wetted Perimeter (ft)`}
          idAndName={`perimeter-details-input`}
          value={props.vm.temporaryChannelDetails.perimeter}
          valid={!props.vm.miscValidations.channelPerimeter}
          className={props.vm.getErrorClass(props.vm.miscValidations.channelPerimeter)}
          type="number"
          onChange={(e) => props.vm.channelDetailsChange(e)}
          onBlur={(e) => props.vm.handleMiscBlur(e)}
        />
      </div>
      <div className="usa-card__footer">
        <ButtonBlock buttons={props.vm.channelDetailsButtons} />
      </div>
    </div>
  );
});
